import axios from 'axios';
import ls from 'localstorage-slim';
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import DataContext from '../context/InvoiceData';
import { refreshToken, getETAToken, updateUserLastLogin } from '../helpers';

function OrderPayment() {
  const {
    step,
    setStep,
    documentType,
    documentTypeVersion,
    setDocumentType,
    setDocumentTypeVersion,
    setInvoiceSubType,
  } = useContext(DataContext);

  let navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentData, setPaymentData] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const subDomain = ls.get('subDomain');
  let invoiceId = localStorage.getItem('id');

  const onChange = nextStep => {
    setStep(nextStep < 0 ? 0 : nextStep > 5 ? 5 : nextStep);
  };

  const handleClose = () => {
    setStep(0);
    setDocumentTypeVersion('');
    setDocumentType('');
    setInvoiceSubType('');
    ls.remove('id');
    navigate('../../document/new');
  };

  const handleContinue = () => {
    const updatePaymentToast = toast.loading('Please wait...');

    axios
      .put(
        `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/payments/${paymentData.id}`,
        paymentData
      )
      .then(() => {
        axios
          .put(`${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/invoices/${invoiceId}`, {
            payment: paymentData.id,
          })
          .then(response => {
            toast.update(updatePaymentToast, {
              render: `${response.data['message']}`,
              type: 'success',
              isLoading: false,
              autoClose: 1000,
              theme: 'colored',
            });
          })
          .then(() => {
            onChange(step + 1);
          })
          .catch(error => {
            toast.update(updatePaymentToast, {
              render: `${error}`,
              type: 'error',
              isLoading: false,
              autoClose: 1000,
              theme: 'colored',
            });
          });
      })
      .catch(error => {
        console.log(error);
      });
  };

  const handlePrevious = () => {
    onChange(step - 1);
  };

  const handleSelectPayment = event => {
    const paymentMethodsCopy = [...paymentMethods];
    const selectedPayment = paymentMethodsCopy.find(
      paymentMethod => paymentMethod.id === +event.target.value
    );
    setPaymentData(selectedPayment);
  };

  const handleEditPaymentData = event => {
    setPaymentData({ ...paymentData, [event.target.name]: event.target.value });
  };

  useEffect(() => {
    async function fetchData() {
      try {
        if (!ls.get('access')) {
          await refreshToken();
          await updateUserLastLogin();
        }
        if (!ls.get('ETAToken')) await getETAToken();

        let response = await axios.get(
          `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/current_taxpayer_payment_methods`,
          {
            params: {
              taxpayer: ls.get('taxpayer_id'),
            },
          }
        );

        setPaymentMethods(response.data);
        setIsLoading(false);
      } catch (error) {
        if (error.response) {
          // Request made but the server responded with an error
          console.log(error.response.data);
        } else if (error.request) {
          // Request made but no response is received from the server.
          console.log(error.request);
        } else {
          // Error occured while setting up the request
          console.log('Error', error.message);
        }
      }
    }

    fetchData();
  }, []);

  return isLoading ? (
    <></>
  ) : (
    <>
      <div className={`${i18n.language === 'ar' && 'text-end'} card border-0 shadow m-3`}>
        <div className='card-body body mx-5 mt-5'>
          <span className='p-2 fw-bold fs-5'>{`${documentType} | ${documentTypeVersion}`}</span>
          <div
            className={`${
              i18n.language === 'en' ? 'row mx-0' : 'd-flex flex-row-reverse'
            } my-3 p-2`}>
            <span
              className='subtitle text-decoration-underline pb-2'
              style={{ textUnderlineOffset: '10px' }}>
              <strong className='fs-5'>{t('stepper.payment.title')}</strong>
            </span>
          </div>
          <div className='row mx-2 my-1'>
            <span className='text-white bg-primary p-1'>{t('stepper.payment.details')}</span>
          </div>
          <div className='row mx-2'>
            <div className={`${i18n.language === 'en' ? 'order-1' : 'order-2'} col-4`}>
              <div className={`row ${i18n.language === 'ar' && 'justify-content-end'} px-2`}>
                {t('stepper.payment.purchaseRef')}
              </div>
              <div className='row px-2'>
                <input
                  type='text'
                  pattern='^[aA-zZ\d- ]+$'
                  title='Only letters and numbers.'
                  className='py-1'
                  dir={`${i18n.language === 'ar' && 'rtl'}`}
                />
              </div>
            </div>
            <div className={`${i18n.language === 'en' ? 'order-2' : 'order-1'} col-8`}>
              <div className={`row ${i18n.language === 'ar' && 'justify-content-end'} px-2`}>
                {t('stepper.payment.purchaseDesc')}
              </div>
              <div className='row px-2'>
                <input
                  type='text'
                  pattern='^[aA-zZ\d- ]+$'
                  title='Only letters and numbers.'
                  className='py-1'
                  dir={`${i18n.language === 'ar' && 'rtl'}`}
                />
              </div>
            </div>
          </div>
          <div className='row mx-2 mt-1 mb-2'>
            <div className={`${i18n.language === 'en' ? 'order-1' : 'order-2'} col-4`}>
              <div className={`row ${i18n.language === 'ar' && 'justify-content-end'} px-2`}>
                {t('stepper.payment.salesRef')}
              </div>
              <div className='row px-2'>
                <input
                  type='text'
                  pattern='^[aA-zZ\d- ]+$'
                  title='Only letters and numbers.'
                  className='py-1'
                  dir={`${i18n.language === 'ar' && 'rtl'}`}
                />
              </div>
            </div>
            <div className={`${i18n.language === 'en' ? 'order-2' : 'order-1'} col-8`}>
              <div className={`row ${i18n.language === 'ar' && 'justify-content-end'} px-2`}>
                {t('stepper.payment.salesDesc')}
              </div>
              <div className='row px-2'>
                <input
                  type='text'
                  pattern='^[aA-zZ\d- ]+$'
                  title='Only letters and numbers.'
                  className='py-1'
                  dir={`${i18n.language === 'ar' && 'rtl'}`}
                />
              </div>
            </div>
          </div>
          <div className='row mx-2 mt-1 mb-2'>
            <div className='col'>
              <div className={`row ${i18n.language === 'ar' && 'justify-content-end'} px-2`}>
                {t('stepper.payment.proforma')}
              </div>
              <div className='row px-2'>
                <input
                  type='text'
                  pattern='^[aA-zZ\d- ]+$'
                  title='Only letters and numbers.'
                  className='py-1'
                  dir={`${i18n.language === 'ar' && 'rtl'}`}
                />
              </div>
            </div>
          </div>
          <div
            className={`row ${
              i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
            } mx-2 mt-1 mb-2 py-1 bg-primary`}>
            <span className='text-white col-7 p-1'>{t('stepper.payment.payment')}</span>
            <select
              id='payment'
              name='payment'
              className='d-inline col-4 mx-2'
              placeholder='Unique Number'
              dir={`${i18n.language === 'ar' && 'rtl'}`}
              onChange={handleSelectPayment}>
              <option value=''>{t('stepper.payment.method')}</option>
              {paymentMethods.map(paymentMethod => (
                <option key={paymentMethod.id} value={paymentMethod.id}>
                  {`${paymentMethod.bank_name}`}
                </option>
              ))}
            </select>
          </div>
          <div className='row mx-2 mt-1 mb-2'>
            <div className={`${i18n.language === 'en' ? 'order-1' : 'order-4'} col`}>
              <div className={`row ${i18n.language === 'ar' && 'justify-content-end'} px-2`}>
                {t('stepper.payment.bankName')}
              </div>
              <div className='row px-2'>
                <input
                  type='text'
                  pattern='^[aA-zZ ]+$'
                  title='Bank name should contain only letters and spaces. e.g. CIB'
                  className='py-1'
                  name='bank_name'
                  defaultValue={paymentData ? paymentData.bank_name : ''}
                  dir={`${i18n.language === 'ar' && 'rtl'}`}
                  onChange={handleEditPaymentData}
                />
              </div>
            </div>
            <div className={`${i18n.language === 'en' ? 'order-2' : 'order-3'} col`}>
              <div className={`row ${i18n.language === 'ar' && 'justify-content-end'} px-2`}>
                {t('stepper.payment.accountNumber')}
              </div>
              <div className='row px-2'>
                <input
                  type='text'
                  className='py-1'
                  pattern='^[aA-zZ\d]+$'
                  title='Bank account number should only contain letters and numbers. e.g. EG242344235423'
                  name='bank_account_number'
                  defaultValue={paymentData ? paymentData.bank_account_number : ''}
                  dir={`${i18n.language === 'ar' && 'rtl'}`}
                  onChange={handleEditPaymentData}
                />
              </div>
            </div>
            <div className={`${i18n.language === 'en' ? 'order-3' : 'order-2'} col`}>
              <div className={`row ${i18n.language === 'ar' && 'justify-content-end'} px-2`}>
                {t('stepper.payment.swift')}
              </div>
              <div className='row px-2'>
                <input
                  type='text'
                  className='py-1'
                  pattern='^[aA-zZ\d]+$'
                  title='Swift code should only contain letters and numbers. e.g. EGTY1323YRTYRTY'
                  name='swift_code'
                  defaultValue={paymentData ? paymentData.swift_code : ''}
                  dir={`${i18n.language === 'ar' && 'rtl'}`}
                  onChange={handleEditPaymentData}
                />
              </div>
            </div>
            <div className={`${i18n.language === 'en' ? 'order-4' : 'order-1'} col`}>
              <div className={`row ${i18n.language === 'ar' && 'justify-content-end'} px-2`}>
                {t('stepper.payment.iban')}
              </div>
              <div className='row px-2'>
                <input
                  type='text'
                  className='py-1'
                  pattern='^[aA-zZ\d]+$'
                  title='Bank account iban should only contain letters and numbers. e.g. EGTY1323YRTYRTY'
                  name='bank_account_iban'
                  defaultValue={paymentData ? paymentData.bank_account_iban : ''}
                  dir={`${i18n.language === 'ar' && 'rtl'}`}
                  onChange={handleEditPaymentData}
                />
              </div>
            </div>
          </div>
          <div className='row mx-2 mt-1'>
            <div className={`${i18n.language === 'en' ? 'order-1' : 'order-2'} col`}>
              <div className={`row ${i18n.language === 'ar' && 'justify-content-end'} px-2`}>
                {t('stepper.payment.address')}
              </div>
              <div className='row px-2'>
                <input
                  type='text'
                  className='py-3'
                  pattern='^[aA-zZ\d-. ]+$'
                  title='Bank address should only contain letters and numbers. e.g. 36 Ashour st.'
                  name='bank_address'
                  defaultValue={paymentData ? paymentData.bank_address : ''}
                  dir={`${i18n.language === 'ar' && 'rtl'}`}
                  onChange={handleEditPaymentData}
                />
              </div>
            </div>
            <div className={`${i18n.language === 'en' ? 'order-2' : 'order-1'} col`}>
              <div className={`row ${i18n.language === 'ar' && 'justify-content-end'} px-2`}>
                {t('stepper.payment.terms')}
              </div>
              <div className='row px-2'>
                <input
                  type='text'
                  className='py-3'
                  pattern='^[aA-zZ\d-.% ]+$'
                  title='Terms should only contain letters and numbers. e.g. 100% in advance'
                  name='terms'
                  defaultValue={paymentData ? paymentData.terms : ''}
                  dir={`${i18n.language === 'ar' && 'rtl'}`}
                  onChange={handleEditPaymentData}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`${
          i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
        } justify-content-evenly fixed-bottom py-3 bg-secondary bg-gradient bg-opacity-10`}>
        <button
          className={`${
            i18n.language === 'ar' && 'offset-6'
          } button text-center col-1 bg-white text-dark border border-1 border-dark`}
          onClick={handleClose}>
          {t('stepper.footer.close')}
        </button>
        <button
          className={`${i18n.language === 'en' && 'offset-6'} button text-center col-1 ${
            step === 0 ? 'bg-secondary bg-gradient' : ''
          }`}
          onClick={handlePrevious}
          disabled={step === 0}>
          {t('stepper.footer.previous')}
        </button>
        <button
          className={`button text-center col-1 ${
            step === 5 || !paymentData ? 'bg-secondary bg-gradient' : ''
          }`}
          onClick={handleContinue}
          disabled={step === 5 || !paymentData}>
          {t('stepper.footer.continue')}
        </button>
      </div>
    </>
  );
}

export default OrderPayment;
